import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { getTaxCodes } from '../lib/api';

/**
 * TaxCodePicker — province-scoped tax-type code picker (Phase 20).
 *
 * Fetches `/api/tax/codes` on mount, renders one dropdown for single-tax
 * provinces (AB/ON/…) or two dropdowns for dual-tax provinces (QC/BC/SK/MB),
 * plus a "No Tax Applicable" toggle.
 *
 * The picker is intentionally category-agnostic: labels are pure tax types
 * (e.g. "GST Zero-Rated"), never product categories.
 *
 * Props:
 *   open       : boolean
 *   initial    : { federal_code, provincial_code, no_tax_applicable } | null
 *   onCancel() : close without apply
 *   onApply(tc)   : called with { federal_code, provincial_code, no_tax_applicable }
 *   onClear()  : called when user hits "Clear override"
 *   titleSuffix: optional appended to title (e.g. "(3 receipts)")
 */
export default function TaxCodePicker({
  open,
  initial = null,
  onCancel,
  onApply,
  onClear,
  titleSuffix = '',
}) {
  const { colors, effective } = useTheme();
  const [catalog, setCatalog] = useState(null);
  const [err, setErr] = useState(null);
  const [fed, setFed] = useState(initial?.federal_code || '');
  const [prov, setProv] = useState(initial?.provincial_code || '');
  const [noTax, setNoTax] = useState(!!initial?.no_tax_applicable);

  // Reset local state whenever the picker is re-opened.
  useEffect(() => {
    if (!open) return;
    setFed(initial?.federal_code || '');
    setProv(initial?.provincial_code || '');
    setNoTax(!!initial?.no_tax_applicable);
  }, [open, initial]);

  useEffect(() => {
    if (!open || catalog) return;
    getTaxCodes()
      .then((c) => setCatalog(c))
      .catch((e) => setErr(String(e?.message || e)));
  }, [open, catalog]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onCancel]);

  if (!open) return null;

  const isDark = effective === 'dark';
  const dual = (catalog?.provincial?.length || 0) > 0;

  const disabled = noTax;

  const modal = (
    <div
      data-testid="taxcode-picker-backdrop"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.45)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        data-testid="taxcode-picker"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          minWidth: 360, maxWidth: 480, width: '100%',
          borderRadius: 20, padding: '20px 22px 18px',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
          backgroundColor: isDark ? 'rgba(20,20,24,0.78)' : 'rgba(255,255,255,0.82)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          boxShadow: isDark
            ? '0 24px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 24px 60px rgba(15,23,42,0.18), inset 0 1px 0 rgba(255,255,255,0.6)',
          color: colors.text,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <h2 data-testid="taxcode-picker-title" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              Set tax code {titleSuffix ? <span style={{ color: colors.textMuted, fontWeight: 500 }}>{titleSuffix}</span> : null}
            </h2>
            {catalog ? (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textMuted }}>
                Province: <b style={{ color: colors.text }}>{catalog.province}</b>
                {dual ? ' · Dual tax' : ' · Single tax'}
              </p>
            ) : null}
          </div>
          <button
            data-testid="taxcode-picker-close"
            onClick={onCancel}
            style={{
              width: 32, height: 32, borderRadius: 8, border: `1px solid ${colors.border}`,
              backgroundColor: 'transparent', color: colors.text, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {err ? (
          <p style={{ color: colors.error, fontSize: 13 }}>Failed to load catalog: {err}</p>
        ) : null}

        {!catalog ? (
          <p style={{ color: colors.textMuted, fontSize: 13 }}>Loading…</p>
        ) : (
          <>
            {/* Federal dropdown */}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>
              Federal
            </label>
            <select
              data-testid="taxcode-federal"
              value={fed}
              disabled={disabled}
              onChange={(e) => setFed(e.target.value)}
              style={{
                width: '100%', height: 44, padding: '0 12px',
                borderRadius: 10, border: `1px solid ${colors.border}`,
                backgroundColor: disabled ? colors.bg : colors.surface,
                color: disabled ? colors.textMuted : colors.text,
                fontSize: 14, fontWeight: 500,
                marginBottom: dual ? 14 : 18,
                cursor: disabled ? 'not-allowed' : 'pointer',
                appearance: 'none',
              }}
            >
              <option value="">— No federal code —</option>
              {catalog.federal.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}{c.rate ? ` (${(c.rate * 100).toFixed(c.rate < 0.1 ? 3 : 0)}%)` : ''}
                </option>
              ))}
            </select>

            {/* Provincial dropdown */}
            {dual ? (
              <>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>
                  Provincial
                </label>
                <select
                  data-testid="taxcode-provincial"
                  value={prov}
                  disabled={disabled}
                  onChange={(e) => setProv(e.target.value)}
                  style={{
                    width: '100%', height: 44, padding: '0 12px',
                    borderRadius: 10, border: `1px solid ${colors.border}`,
                    backgroundColor: disabled ? colors.bg : colors.surface,
                    color: disabled ? colors.textMuted : colors.text,
                    fontSize: 14, fontWeight: 500, marginBottom: 18,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    appearance: 'none',
                  }}
                >
                  <option value="">— No provincial code —</option>
                  {catalog.provincial.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}{c.rate ? ` (${(c.rate * 100).toFixed(c.rate < 0.1 ? 3 : 0)}%)` : ''}
                    </option>
                  ))}
                </select>
              </>
            ) : null}

            {/* No tax applicable */}
            <label
              data-testid="taxcode-no-tax-toggle"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${noTax ? colors.accent : colors.border}`,
                backgroundColor: noTax ? `${colors.accent}18` : 'transparent',
                cursor: 'pointer',
                marginBottom: 14,
              }}
            >
              <input
                type="checkbox"
                checked={noTax}
                onChange={(e) => setNoTax(e.target.checked)}
                style={{ accentColor: colors.accent, width: 16, height: 16 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>No Tax Applicable</div>
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                  Force zero tax regardless of codes above.
                </div>
              </div>
            </label>
          </>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {onClear ? (
            <button
              data-testid="taxcode-clear"
              onClick={onClear}
              style={{
                height: 40, padding: '0 14px', borderRadius: 10,
                border: `1px solid ${colors.border}`, backgroundColor: 'transparent',
                color: colors.textMuted, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Clear override
            </button>
          ) : null}
          <div style={{ flex: 1 }} />
          <button
            data-testid="taxcode-cancel"
            onClick={onCancel}
            style={{
              height: 40, minWidth: 88, padding: '0 16px', borderRadius: 10,
              border: `1px solid ${colors.border}`, backgroundColor: 'transparent',
              color: colors.text, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            data-testid="taxcode-apply"
            onClick={() => onApply?.({
              federal_code: fed || null,
              provincial_code: prov || null,
              no_tax_applicable: !!noTax,
            })}
            disabled={!catalog}
            style={{
              height: 40, minWidth: 100, padding: '0 18px', borderRadius: 10,
              border: 'none', backgroundColor: colors.accent, color: '#FFFFFF',
              fontSize: 14, fontWeight: 700, cursor: catalog ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
              opacity: catalog ? 1 : 0.5,
              boxShadow: '0 1px 0 rgba(255,255,255,0.14) inset',
            }}
          >
            <Check size={14} /> Apply
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}


/**
 * Small helper — pill-style renderer of a TaxCodeOverride value. Used on
 * ReceiptDetail to show the currently-set codes.
 */
export function TaxCodePills({ value, catalog, colors }) {
  if (!value) return null;
  if (value.no_tax_applicable) {
    return (
      <span
        data-testid="taxcode-pill-no-tax"
        style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
          backgroundColor: '#0EA5E9', color: '#FFFFFF',
        }}
      >
        No Tax Applicable
      </span>
    );
  }
  const map = new Map();
  (catalog?.federal || []).forEach((c) => map.set(c.code, c.label));
  (catalog?.provincial || []).forEach((c) => map.set(c.code, c.label));
  const pieces = [value.federal_code, value.provincial_code].filter(Boolean);
  if (pieces.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {pieces.map((code) => (
        <span
          key={code}
          data-testid={`taxcode-pill-${code}`}
          style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
            backgroundColor: colors?.accent || '#22C55E', color: '#FFFFFF',
          }}
        >
          {map.get(code) || code}
        </span>
      ))}
    </span>
  );
}
